import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, Upload, Trash2, FileText, Users, Award, Search, Loader2,
  Plus, Pencil, Image as ImageIcon, Mail, MailOpen, Check, Star, X,
} from "lucide-react";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";

const DEFAULT_QSL_FIELDS = {
  callsign: { x: 0.229, y: 0.889, size: 0.045, color: "#1C1C28", font: "mono" },
  date:     { x: 0.361, y: 0.889, size: 0.045, color: "#1C1C28", font: "mono" },
  time:     { x: 0.448, y: 0.889, size: 0.045, color: "#1C1C28", font: "mono" },
  band:     { x: 0.529, y: 0.889, size: 0.045, color: "#1C1C28", font: "mono" },
  mode:     { x: 0.608, y: 0.889, size: 0.045, color: "#1C1C28", font: "mono" },
  rst:      { x: 0.683, y: 0.889, size: 0.045, color: "#1C1C28", font: "mono" },
};
const QSL_FIELD_ORDER = ["callsign", "date", "time", "band", "mode", "rst"];
const QSL_FIELD_LABELS = {
  callsign: "Indicativo",
  date:     "Fecha",
  time:     "UTC",
  band:     "Banda",
  mode:     "Modo",
  rst:      "RST",
};
const QSL_FIELD_SAMPLES = {
  callsign: "EA3RKO",
  date:     "24/07/26",
  time:     "20:15",
  band:     "20M",
  mode:     "SSB",
  rst:      "59",
};

const DEFAULT_LEVELS = [
  { name: "Bronce",  required_contacts: 5 },
  { name: "Plata",   required_contacts: 10 },
  { name: "Oro",     required_contacts: 20 },
  { name: "Platino", required_contacts: 40 },
];

const EMPTY = {
  name: "", type: "diploma", station_callsign: "", subtitle: "",
  inscription:
    'El radioclub ochentero otorga el presente diploma a la estación "{callsign}" por haber conseguido los contactos necesarios para la obtención del presente diploma.',
  required_contacts: 1, required_activations: 0,
  required_band: "", required_mode: "", overlay_mode: "full", active: true, allow_duplicates: false,
  bases: "",
  callsign_x: 0.5, callsign_y: 0.5, callsign_font: "mono", callsign_color: "#000000", callsign_size: 0.10,
  qsl_fields: JSON.parse(JSON.stringify(DEFAULT_QSL_FIELDS)),
  levels: DEFAULT_LEVELS.map((l) => ({ ...l })),
};

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-md border border-zinc-400 bg-white px-3 py-2.5 min-w-0">
      <p className="text-[10px] sm:text-xs uppercase tracking-[0.15em] text-black font-semibold leading-tight">{label}</p>
      <p data-testid={testid} className="font-mono text-2xl font-bold mt-0.5 text-[#5EA529]">{value}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total_contacts: 0, total_logs: 0, total_diplomas: 0 });
  const [diplomas, setDiplomas] = useState([]);
  const [logs, setLogs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [csearch, setCsearch] = useState("");
  const [cdiploma, setCdiploma] = useState("all");
  const [dupOnly, setDupOnly] = useState(false);
  const [dlOnly, setDlOnly] = useState(false);
  const [contactTotal, setContactTotal] = useState(0);
  const [contactDownloaded, setContactDownloaded] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadDiploma, setUploadDiploma] = useState("");
  const [activatorCall, setActivatorCall] = useState("");
  const logInput = useRef();
  const tplInput = useRef();
  const [tplTarget, setTplTarget] = useState(null);

  const [editing, setEditing] = useState(null); // diploma being edited or null
  const [previewLevel, setPreviewLevel] = useState(0);
  const [activeQslField, setActiveQslField] = useState("callsign");
  const levelInputRef = useRef();
  const [levelUploadTarget, setLevelUploadTarget] = useState(null); // {diploma_id, level_index}
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [onlyActive, setOnlyActive] = useState(false);
  const [typeFilter, setTypeFilter] = useState("all"); // "all" | "public" | "activator"
  const [messages, setMessages] = useState([]);
  const [msgUnread, setMsgUnread] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [notifUnread, setNotifUnread] = useState(0);

  const loadStats = () => api.get("/admin/stats").then(({ data }) => setStats(data));
  const loadDiplomas = () => api.get("/admin/diplomas").then(({ data }) => setDiplomas(data));
  const loadLogs = () => api.get("/admin/logs").then(({ data }) => setLogs(data));
  const loadContacts = (q = csearch, did = cdiploma, dl = dlOnly) =>
    api.get("/admin/contacts", { params: { search: q, diploma_id: did === "all" ? "" : did, downloaded_only: dl } }).then(({ data }) => {
      setContacts(data.contacts);
      setContactTotal(data.total || 0);
      setContactDownloaded(data.downloaded || 0);
    });
  const shownContacts = dupOnly ? contacts.filter((c) => c.is_duplicate) : contacts;
  const shownTotal = dupOnly ? shownContacts.length : contactTotal;
  const shownDownloaded = dupOnly
    ? new Set(shownContacts.filter((c) => c.downloaded).map((c) => c.call)).size
    : contactDownloaded;
  const loadMessages = () =>
    api.get("/admin/messages").then(({ data }) => {
      setMessages(data.messages || []);
      setMsgUnread(data.unread || 0);
    });
  const loadNotifications = () =>
    api.get("/admin/notifications").then(({ data }) => {
      setNotifications(data.notifications || []);
      setNotifUnread(data.unread || 0);
    });

  useEffect(() => {
    loadStats();
    loadDiplomas();
    loadLogs();
    loadContacts();
    loadMessages();
    loadNotifications();
  }, []);

  const doLogout = () => {
    logout();
    navigate("/admin/login");
  };

  // ---- diploma editor ----
  const openNew = () => {
    setEditing({});
    setForm(EMPTY);
  };
  const openEdit = (d) => {
    setEditing(d);
    setForm({
      name: d.name || "", type: d.type || "diploma",
      station_callsign: d.station_callsign || "", subtitle: d.subtitle || "",
      inscription: d.inscription || EMPTY.inscription,
      required_contacts: d.required_contacts || 1,
      required_activations: d.required_activations || 0,
      required_band: d.required_band || "", required_mode: d.required_mode || "",
      overlay_mode: d.overlay_mode || "full",
      active: d.active !== false,
      allow_duplicates: d.allow_duplicates === true,
      bases: d.bases || "",
      callsign_x: typeof d.callsign_x === "number" ? d.callsign_x : 0.5,
      callsign_y: typeof d.callsign_y === "number" ? d.callsign_y : 0.5,
      callsign_font: d.callsign_font || "mono",
      callsign_color: d.callsign_color || "#000000",
      callsign_size: typeof d.callsign_size === "number" ? d.callsign_size : 0.10,
      qsl_fields: (() => {
        const stored = d.qsl_fields && typeof d.qsl_fields === "object" ? d.qsl_fields : {};
        const out = {};
        QSL_FIELD_ORDER.forEach((k) => {
          out[k] = { ...DEFAULT_QSL_FIELDS[k], ...(stored[k] || {}) };
        });
        return out;
      })(),
      levels: (Array.isArray(d.levels) && d.levels.length > 0)
        ? d.levels.map((l, i) => ({
            name: l.name || DEFAULT_LEVELS[i]?.name || `Nivel ${i + 1}`,
            required_contacts: Number(l.required_contacts) || DEFAULT_LEVELS[i]?.required_contacts || 0,
            has_template: !!l.has_template,
          }))
        : DEFAULT_LEVELS.map((l) => ({ ...l, has_template: false })),
    });
  };
  const toggleActive = async (d) => {
    try {
      await api.patch(`/admin/diplomas/${d.id}/active`, { active: !d.active });
      loadDiplomas();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const saveDiploma = async () => {
    setSaving(true);
    const payload = {
      ...form,
      required_contacts: Number(form.required_contacts) || 1,
      required_activations: Number(form.required_activations) || 0,
    };
    const isCreate = !(editing && editing.id);
    try {
      let saved;
      if (!isCreate) {
        const { data } = await api.put(`/admin/diplomas/${editing.id}`, payload, { timeout: 20000 });
        saved = data;
      } else {
        const { data } = await api.post("/admin/diplomas", payload, { timeout: 30000 });
        saved = data;
      }
      toast.success("Diploma guardado");
      if (saved && saved.id) setEditing(saved);
      else setEditing(null);
      loadDiplomas();
      loadStats();
    } catch (err) {
      // The POST may have succeeded on the backend even if the response was lost
      // (Cloudflare 520, cold-start timeout, etc). Reload the list and look for a
      // just-created diploma with the same name+type to avoid duplicates on retry.
      if (isCreate) {
        try {
          const { data: list } = await api.get("/admin/diplomas");
          const match = (list || []).find(
            (d) => d.name === payload.name && d.type === payload.type && !d.has_template
          );
          if (match) {
            toast.success("Diploma guardado (la respuesta tardó, pero se creó correctamente)");
            setEditing(match);
            loadDiplomas();
            loadStats();
            return;
          }
        } catch (_) { /* ignore */ }
      }
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  const deleteDiploma = async (id) => {
    if (!window.confirm("¿Eliminar este diploma y sus logs asociados?")) return;
    await api.delete(`/admin/diplomas/${id}`);
    toast.success("Diploma eliminado");
    loadDiplomas();
    loadLogs();
    loadStats();
    loadContacts(csearch);
  };

  // ---- template upload ----
  const pickTemplate = (id) => {
    setTplTarget(id);
    tplInput.current?.click();
  };
  const uploadTemplate = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !tplTarget) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/diplomas/${tplTarget}/template`, fd);
      toast.success("Plantilla actualizada");
      loadDiplomas();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      if (tplInput.current) tplInput.current.value = "";
      setTplTarget(null);
    }
  };

  // ---- multidiploma per-level template upload ----
  const pickLevelTemplate = (diploma_id, level_index) => {
    setLevelUploadTarget({ diploma_id, level_index });
    levelInputRef.current?.click();
  };
  const uploadLevelTemplate = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !levelUploadTarget) return;
    const { diploma_id, level_index } = levelUploadTarget;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/diplomas/${diploma_id}/template?level_index=${level_index}`, fd);
      toast.success("Plantilla del nivel actualizada");
      // Refresh the editing form so the thumbnail updates
      const fresh = await api.get(`/admin/diplomas`);
      const updated = fresh.data.find((d) => d.id === diploma_id);
      if (updated) {
        setEditing(updated);
        setForm((prev) => ({
          ...prev,
          levels: (updated.levels || []).map((l, i) => ({
            name: l.name || DEFAULT_LEVELS[i]?.name || `Nivel ${i + 1}`,
            required_contacts: Number(l.required_contacts) || 0,
            has_template: !!l.has_template,
          })),
        }));
      }
      loadDiplomas();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      if (levelInputRef.current) levelInputRef.current.value = "";
      setLevelUploadTarget(null);
    }
  };

  // ---- log upload ----
  const uploadLog = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!uploadDiploma) {
      toast.error("Selecciona primero el diploma");
      if (logInput.current) logInput.current.value = "";
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("diploma_id", uploadDiploma);
    if (activatorCall.trim()) fd.append("activator_callsign", activatorCall.trim());
    try {
      const { data } = await api.post("/admin/logs", fd);
      toast.success(`${data.contact_count} contactos importados`);
      loadLogs();
      loadStats();
      loadContacts(csearch);
      loadDiplomas();
      loadNotifications();
      setActivatorCall("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (logInput.current) logInput.current.value = "";
    }
  };
  const deleteLog = async (id) => {
    await api.delete(`/admin/logs/${id}`);
    toast.success("Log eliminado");
    loadLogs();
    loadStats();
    loadContacts(csearch);
    loadDiplomas();
  };

  // ---- messages ----
  const markRead = async (id) => {
    try {
      await api.patch(`/admin/messages/${id}/read`);
      loadMessages();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const deleteMessage = async (id) => {
    if (!window.confirm("¿Eliminar este mensaje?")) return;
    await api.delete(`/admin/messages/${id}`);
    toast.success("Mensaje eliminado");
    loadMessages();
  };

  // ---- notifications (activator earned) ----
  const markNotifRead = async (id) => {
    try {
      await api.patch(`/admin/notifications/${id}/read`);
      loadNotifications();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const deleteNotification = async (id) => {
    if (!window.confirm("¿Eliminar esta notificación?")) return;
    await api.delete(`/admin/notifications/${id}`);
    toast.success("Notificación eliminada");
    loadNotifications();
  };

  const inputCls = "mt-1.5 bg-white border-zinc-400 rounded-sm font-mono focus-visible:ring-[#5EA529]";

  return (
    <div className="min-h-screen bg-[#D6D3D1] text-zinc-900">
      <header className="border-b border-zinc-400 sticky top-0 bg-[#D6D3D1]/90 backdrop-blur z-10">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/logo.png" alt="Radio Club" className="h-9 w-auto" />
            <span className="font-mono font-bold tracking-tighter text-lg">QSL·LOG Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-zinc-600 font-mono hidden sm:block">{user?.email}</span>
            <Button data-testid="logout-btn" onClick={doLogout} variant="ghost" className="text-zinc-600 hover:text-[#5EA529] hover:bg-transparent">
              <LogOut className="h-4 w-4 mr-2" /> Salir
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-8">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          <Stat label="Diplomas" value={stats.total_diplomas} testid="stat-diplomas" />
          <Stat label="Logs" value={stats.total_logs} testid="stat-logs" />
          <Stat label="Contactos" value={shownTotal} testid="stat-contacts" />
          <Stat label="Diplomas descargados" value={shownDownloaded} testid="stat-downloaded" />
        </div>

        <Tabs defaultValue="diplomas">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <TabsList className="bg-white border border-zinc-400">
              <TabsTrigger value="diplomas" data-testid="tab-diplomas" className="font-mono data-[state=active]:bg-[#5EA529] data-[state=active]:text-white">
                <Award className="h-4 w-4 mr-1.5" /> Diplomas
              </TabsTrigger>
              <TabsTrigger value="logs" data-testid="tab-logs" className="font-mono data-[state=active]:bg-[#5EA529] data-[state=active]:text-white">
                <FileText className="h-4 w-4 mr-1.5" /> Logs
              </TabsTrigger>
              <TabsTrigger value="contacts" data-testid="tab-contacts" className="font-mono data-[state=active]:bg-[#5EA529] data-[state=active]:text-white">
                <Users className="h-4 w-4 mr-1.5" /> Contactos
              </TabsTrigger>
              <TabsTrigger value="messages" data-testid="tab-messages" className="font-mono data-[state=active]:bg-[#5EA529] data-[state=active]:text-white relative">
                <Mail className="h-4 w-4 mr-1.5" /> Mensajes
                {msgUnread > 0 && (
                  <span data-testid="messages-unread-badge" className="ml-1.5 inline-flex items-center justify-center bg-red-600 text-white text-[10px] font-bold rounded-full h-4 min-w-4 px-1">
                    {msgUnread}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="notifications" data-testid="tab-notifications" className="font-mono data-[state=active]:bg-[#5EA529] data-[state=active]:text-white relative">
                <Star className="h-4 w-4 mr-1.5" /> Notificaciones
                {notifUnread > 0 && (
                  <span data-testid="notifications-unread-badge" className="ml-1.5 inline-flex items-center justify-center bg-red-600 text-white text-[10px] font-bold rounded-full h-4 min-w-4 px-1">
                    {notifUnread}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>
            <div className="flex items-center gap-2 flex-wrap ml-auto">
              <div className="inline-flex rounded-sm border border-zinc-400 bg-white overflow-hidden font-mono text-xs" data-testid="type-filter-group">
                <button
                  data-testid="type-filter-all"
                  onClick={() => setTypeFilter("all")}
                  className={`px-3 py-1.5 transition-colors ${typeFilter === "all" ? "bg-[#5EA529] text-white" : "text-black hover:bg-zinc-100"}`}
                >
                  Todos
                </button>
                <button
                  data-testid="type-filter-public"
                  onClick={() => setTypeFilter("public")}
                  className={`px-3 py-1.5 border-l border-zinc-400 transition-colors ${typeFilter === "public" ? "bg-[#5EA529] text-white" : "text-black hover:bg-zinc-100"}`}
                >
                  Diplomas / QSL
                </button>
                <button
                  data-testid="type-filter-activator"
                  onClick={() => setTypeFilter("activator")}
                  className={`px-3 py-1.5 border-l border-zinc-400 transition-colors ${typeFilter === "activator" ? "bg-[#5EA529] text-white" : "text-black hover:bg-zinc-100"}`}
                >
                  Activador
                </button>
              </div>
              <label className="flex items-center gap-2 text-sm font-mono text-black cursor-pointer">
                <Switch data-testid="filter-active-switch" checked={onlyActive} onCheckedChange={setOnlyActive} className="data-[state=checked]:bg-[#5EA529]" />
                Ver solo activos
              </label>
            </div>
          </div>

          {/* DIPLOMAS */}
          <TabsContent value="diplomas" className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-mono text-lg font-semibold tracking-tight">Diplomas y QSL</h2>
              <Button data-testid="new-diploma-btn" onClick={openNew} className="bg-[#5EA529] hover:bg-[#4f8c22] text-white font-semibold rounded-sm transition-colors">
                <Plus className="h-4 w-4 mr-2" /> Nuevo
              </Button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {(() => {
                const list = diplomas.filter((d) => {
                  if (onlyActive && !d.active) return false;
                  if (typeFilter === "public" && d.type === "activator") return false;
                  if (typeFilter === "activator" && d.type !== "activator") return false;
                  return true;
                });
                if (list.length === 0) {
                  const emptyMsg =
                    typeFilter === "activator"
                      ? (onlyActive ? "No hay diplomas de activador activos." : "No hay diplomas de activador. Crea uno con “Nuevo”.")
                      : typeFilter === "public"
                        ? (onlyActive ? "No hay diplomas/QSL activos." : "No hay diplomas ni QSL. Crea uno con “Nuevo”.")
                        : (onlyActive ? "No hay diplomas activos." : "No hay diplomas. Crea el primero con “Nuevo”.");
                  return (
                    <p data-testid="empty-diplomas-message" className="text-zinc-600 text-sm col-span-2 py-8 text-center">
                      {emptyMsg}
                    </p>
                  );
                }
                return list.map((d) => (
                <div key={d.id} data-testid={`diploma-card-${d.id}`} className={`rounded-md border border-zinc-400 bg-white overflow-hidden ${d.active ? "" : "opacity-60"}`}>
                  <div className="aspect-[3/2] bg-zinc-100 flex items-center justify-center overflow-hidden">
                    {d.has_template ? (
                      <img src={`${API}/diplomas/${d.id}/template?t=${Date.now()}`} alt={d.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="text-center text-zinc-600">
                        <ImageIcon className="h-8 w-8 mx-auto mb-1" />
                        <span className="text-xs">Sin plantilla</span>
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs uppercase tracking-[0.2em] text-[#5EA529]">
                        {d.type === "qsl" ? "QSL" : d.type === "activator" ? "Activador" : "Diploma"}
                        {d.station_callsign ? ` · ${d.station_callsign}` : ""}
                      </span>
                      <span className="text-xs font-mono text-zinc-600">
                        {d.contact_count} contactos · {d.log_count} logs
                      </span>
                    </div>
                    <h3 className="font-mono font-semibold text-lg">{d.name}</h3>
                    <p className="text-sm text-zinc-600 mb-3">
                      {d.type === "activator" ? (
                        <>{d.required_activations || 0} activaciones</>
                      ) : (
                        <>
                          {d.required_contacts} contacto(s)
                          {d.required_band ? ` · ${d.required_band.toUpperCase()}` : ""}
                          {d.required_mode ? ` · ${d.required_mode}` : ""}
                        </>
                      )}
                    </p>
                    <label className="flex items-center gap-2 text-sm font-mono mb-3 cursor-pointer w-fit">
                      <Switch data-testid={`active-diploma-${d.id}`} checked={d.active !== false} onCheckedChange={() => toggleActive(d)} className="data-[state=checked]:bg-[#5EA529]" />
                      <span className={d.active !== false ? "text-[#5EA529] font-semibold" : "text-zinc-500"}>
                        {d.active !== false ? "Activo" : "Inactivo"}
                      </span>
                    </label>
                    <div className="flex gap-2">
                      <Button data-testid={`edit-diploma-${d.id}`} onClick={() => openEdit(d)} variant="outline" size="sm" className="border-zinc-400 bg-white hover:bg-zinc-200 hover:text-[#5EA529] rounded-sm">
                        <Pencil className="h-3.5 w-3.5 mr-1.5" /> Editar
                      </Button>
                      <Button data-testid={`template-diploma-${d.id}`} onClick={() => pickTemplate(d.id)} variant="outline" size="sm" className="border-zinc-400 bg-white hover:bg-zinc-200 hover:text-[#5EA529] rounded-sm">
                        <ImageIcon className="h-3.5 w-3.5 mr-1.5" /> Plantilla
                      </Button>
                      <Button data-testid={`delete-diploma-${d.id}`} onClick={() => deleteDiploma(d.id)} variant="ghost" size="sm" className="text-zinc-600 hover:text-red-400 hover:bg-transparent ml-auto">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
                ));
              })()}
            </div>
            <input ref={tplInput} type="file" accept="image/png,image/jpeg,image/webp" onChange={uploadTemplate} className="hidden" data-testid="template-file-input" />
            <input ref={levelInputRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={uploadLevelTemplate} className="hidden" data-testid="level-template-file-input" />
          </TabsContent>

          {/* LOGS */}
          <TabsContent value="logs" className="mt-6">
            <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-4">
              <div className="flex-1 max-w-xs">
                <Label className="text-xs uppercase tracking-[0.2em] text-black">Diploma destino</Label>
                <Select value={uploadDiploma} onValueChange={setUploadDiploma}>
                  <SelectTrigger data-testid="log-diploma-select" className={inputCls}>
                    <SelectValue placeholder="Selecciona diploma" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-zinc-400 text-zinc-900">
                    {diplomas.filter((d) => d.type !== "activator").map((d) => (
                      <SelectItem key={d.id} value={d.id} className="text-zinc-900">{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 max-w-xs">
                <Label className="text-xs uppercase tracking-[0.2em] text-black">
                  Indicativo del activador <span className="text-zinc-500 lowercase tracking-normal">(opcional)</span>
                </Label>
                <Input
                  data-testid="activator-callsign-input"
                  value={activatorCall}
                  onChange={(e) => setActivatorCall(e.target.value.toUpperCase())}
                  placeholder="EA3XXX"
                  className={inputCls}
                  maxLength={20}
                />
              </div>
              <input ref={logInput} type="file" accept=".adi,.adif,text/plain" onChange={uploadLog} className="hidden" data-testid="log-file-input" />
              <Button data-testid="upload-log-btn" onClick={() => logInput.current?.click()} disabled={uploading || !uploadDiploma} className="bg-[#5EA529] hover:bg-[#4f8c22] text-white font-semibold rounded-sm transition-colors disabled:opacity-40">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                Subir ADIF
              </Button>
              {uploadDiploma && (
                <Button data-testid="clear-log-filter-btn" onClick={() => setUploadDiploma("")} variant="outline" className="border-zinc-400 bg-white hover:bg-zinc-200 hover:text-[#5EA529] rounded-sm text-black">
                  Ver todos
                </Button>
              )}
            </div>
            {uploadDiploma && (
              <p className="text-xs font-mono text-zinc-600 mb-3">Mostrando solo los logs del diploma seleccionado.</p>
            )}
            <div className="rounded-md border border-zinc-400 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-b-2 border-black hover:bg-transparent">
                    <TableHead className="text-black font-mono text-sm font-bold uppercase">Archivo</TableHead>
                    <TableHead className="text-black font-mono text-sm font-bold uppercase">Diploma</TableHead>
                    <TableHead className="text-black font-mono text-sm font-bold uppercase">Contactos</TableHead>
                    <TableHead className="text-black font-mono text-sm font-bold uppercase">Duplicados</TableHead>
                    <TableHead className="text-black font-mono text-sm font-bold uppercase">Fecha</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.filter((l) => !uploadDiploma || l.diploma_id === uploadDiploma).length === 0 && (
                    <TableRow className="border-b-2 border-black">
                      <TableCell colSpan={6} className="text-center text-zinc-600 py-8">
                        {uploadDiploma ? "No hay logs para el diploma seleccionado." : "No hay logs subidos."}
                      </TableCell>
                    </TableRow>
                  )}
                  {logs.filter((l) => !uploadDiploma || l.diploma_id === uploadDiploma).map((l) => (
                    <TableRow key={l.id} data-testid={`log-row-${l.id}`} className="border-b-2 border-black hover:bg-zinc-200/70">
                      <TableCell className="font-mono text-black">{l.filename}</TableCell>
                      <TableCell className="font-mono text-black">{l.diploma_name}</TableCell>
                      <TableCell className="font-mono text-black font-semibold">{l.contact_count}</TableCell>
                      <TableCell className={`font-mono font-bold ${l.duplicate_count > 0 ? "text-red-600" : "text-zinc-400"}`} data-testid={`log-dups-${l.id}`}>
                        {l.duplicate_count > 0 ? l.duplicate_count : "—"}
                      </TableCell>
                      <TableCell className="font-mono text-black text-sm">{new Date(l.uploaded_at).toLocaleString("es-ES")}</TableCell>
                      <TableCell className="text-right">
                        <Button data-testid={`delete-log-${l.id}`} onClick={() => deleteLog(l.id)} variant="ghost" size="icon" className="text-zinc-600 hover:text-red-400 hover:bg-transparent">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* CONTACTS */}
          <TabsContent value="contacts" className="mt-6">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <div className="relative flex-1 min-w-[200px] max-w-sm">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
                <Input
                  data-testid="contact-search-input"
                  value={csearch}
                  onChange={(e) => { setCsearch(e.target.value); loadContacts(e.target.value, cdiploma); }}
                  placeholder="Buscar indicativo…"
                  className="pl-9 bg-white border-zinc-400 rounded-sm font-mono focus-visible:ring-[#5EA529]"
                />
              </div>
              <Select value={cdiploma} onValueChange={(v) => { setCdiploma(v); loadContacts(csearch, v); }}>
                <SelectTrigger data-testid="contact-diploma-filter" className="w-[220px] bg-white border-zinc-400 rounded-sm text-zinc-900">
                  <SelectValue placeholder="Todos los diplomas" />
                </SelectTrigger>
                <SelectContent className="bg-white border-zinc-400 text-zinc-900">
                  <SelectItem value="all">Todos los diplomas</SelectItem>
                  {diplomas.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <label className="flex items-center gap-2 text-sm text-zinc-700 font-mono">
                <Switch data-testid="contact-dup-filter" checked={dupOnly} onCheckedChange={setDupOnly} className="data-[state=checked]:bg-red-600" />
                Solo duplicados
              </label>
              <label className="flex items-center gap-2 text-sm text-zinc-700 font-mono">
                <Switch data-testid="contact-downloaded-filter" checked={dlOnly} onCheckedChange={(v) => { setDlOnly(v); loadContacts(csearch, cdiploma, v); }} className="data-[state=checked]:bg-[#5EA529]" />
                Solo descargados
              </label>
            </div>
            {(() => {
              const shown = shownContacts;
              return (
            <div className="rounded-md border border-zinc-400 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-400 hover:bg-transparent">
                    <TableHead className="text-zinc-600 font-mono text-xs uppercase">Indicativo</TableHead>
                    <TableHead className="text-zinc-600 font-mono text-xs uppercase">Diploma</TableHead>
                    <TableHead className="text-zinc-600 font-mono text-xs uppercase">Banda</TableHead>
                    <TableHead className="text-zinc-600 font-mono text-xs uppercase">Modo</TableHead>
                    <TableHead className="text-zinc-600 font-mono text-xs uppercase">Fecha</TableHead>
                    <TableHead className="text-zinc-600 font-mono text-xs uppercase">Hora</TableHead>
                    <TableHead className="text-zinc-600 font-mono text-xs uppercase">Estado</TableHead>
                    <TableHead className="text-zinc-600 font-mono text-xs uppercase text-center">Descargado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {shown.length === 0 && (
                    <TableRow className="border-zinc-400">
                      <TableCell colSpan={8} className="text-center text-zinc-600 py-8">
                        {dupOnly ? "No hay contactos duplicados." : dlOnly ? "Ningún contacto ha descargado todavía." : "Sin contactos."}
                      </TableCell>
                    </TableRow>
                  )}
                  {shown.map((c, i) => (
                    <TableRow key={i} data-testid={`contact-row-${i}`} className={`border-zinc-400 hover:bg-zinc-200/70 ${c.is_duplicate ? "bg-red-50" : ""}`}>
                      <TableCell className={`font-mono font-semibold ${c.is_duplicate ? "text-red-600" : ""}`}>{c.call}</TableCell>
                      <TableCell className={`font-mono text-sm ${c.is_duplicate ? "text-red-600" : "text-zinc-600"}`}>{diplomas.find((d) => d.id === c.diploma_id)?.name || "—"}</TableCell>
                      <TableCell className={`font-mono ${c.is_duplicate ? "text-red-600" : "text-zinc-600"}`}>{c.band?.toUpperCase()}</TableCell>
                      <TableCell className={`font-mono ${c.is_duplicate ? "text-red-600" : "text-zinc-600"}`}>{c.mode}</TableCell>
                      <TableCell className={`font-mono text-sm ${c.is_duplicate ? "text-red-600" : "text-zinc-600"}`}>{c.qso_date}</TableCell>
                      <TableCell className={`font-mono text-sm ${c.is_duplicate ? "text-red-600" : "text-zinc-600"}`}>{c.time_on}</TableCell>
                      <TableCell className="font-mono text-xs">
                        {c.is_duplicate
                          ? <span className="text-red-600 font-bold uppercase">Duplicado</span>
                          : <span className="text-[#5EA529]">Válido</span>}
                      </TableCell>
                      <TableCell className="text-center" data-testid={`contact-downloaded-${i}`}>
                        {c.downloaded
                          ? <Check className="h-4 w-4 text-[#5EA529] inline" strokeWidth={3} aria-label="Descargado" />
                          : <X className="h-4 w-4 text-red-600 inline" strokeWidth={3} aria-label="No descargado" />}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
              );
            })()}
          </TabsContent>

          {/* MESSAGES */}
          <TabsContent value="messages" className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-mono text-lg font-semibold tracking-tight">
                Mensajes de contacto{" "}
                <span className="text-sm text-zinc-600 font-normal">({messages.length})</span>
              </h2>
              {msgUnread > 0 && (
                <span className="text-xs font-mono text-red-600 font-semibold">
                  {msgUnread} sin leer
                </span>
              )}
            </div>
            {messages.length === 0 ? (
              <p className="text-center text-zinc-600 py-10">
                No hay mensajes recibidos todavía.
              </p>
            ) : (
              <div className="space-y-3">
                {messages.map((m) => (
                  <div
                    key={m.id}
                    data-testid={`message-${m.id}`}
                    className={`rounded-md border p-4 ${
                      m.read ? "bg-white border-zinc-400" : "bg-white border-[#5EA529] shadow-sm"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          {!m.read && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-white bg-red-600 rounded-full px-2 py-0.5">
                              Nuevo
                            </span>
                          )}
                          <h3 className="font-mono font-bold text-black">{m.name}</h3>
                        </div>
                        <a
                          href={`mailto:${m.email}?subject=Radio Club Ochentero`}
                          className="text-sm text-[#5EA529] font-mono hover:underline"
                          data-testid={`message-reply-${m.id}`}
                        >
                          {m.email}
                        </a>
                      </div>
                      <span className="text-xs font-mono text-zinc-600 shrink-0">
                        {new Date(m.created_at).toLocaleString("es-ES")}
                      </span>
                    </div>
                    <p className="text-sm text-zinc-800 whitespace-pre-wrap leading-relaxed mt-2 border-t border-zinc-200 pt-3">
                      {m.message}
                    </p>
                    <div className="flex gap-2 mt-3">
                      {!m.read && (
                        <Button
                          onClick={() => markRead(m.id)}
                          variant="outline"
                          size="sm"
                          className="border-zinc-400 bg-white hover:bg-zinc-100 rounded-sm text-black"
                          data-testid={`mark-read-${m.id}`}
                        >
                          <Check className="h-3.5 w-3.5 mr-1.5" /> Marcar leído
                        </Button>
                      )}
                      {m.read && (
                        <span className="text-xs text-zinc-500 font-mono inline-flex items-center gap-1">
                          <MailOpen className="h-3.5 w-3.5" /> Leído
                        </span>
                      )}
                      <Button
                        onClick={() => deleteMessage(m.id)}
                        variant="ghost"
                        size="sm"
                        className="text-zinc-600 hover:text-red-400 hover:bg-transparent ml-auto"
                        data-testid={`delete-message-${m.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* NOTIFICATIONS (activator earned) */}
          <TabsContent value="notifications" className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-mono text-lg font-semibold tracking-tight">
                Notificaciones{" "}
                <span className="text-sm text-zinc-600 font-normal">({notifications.length})</span>
              </h2>
              {notifUnread > 0 && (
                <span className="text-xs font-mono text-red-600 font-semibold">
                  {notifUnread} sin leer
                </span>
              )}
            </div>
            {notifications.length === 0 ? (
              <p className="text-center text-zinc-600 py-10">
                Aún no hay notificaciones. Cuando un activador alcance un nivel, aparecerá aquí.
              </p>
            ) : (
              <div className="space-y-3">
                {notifications.map((n) => (
                  <div
                    key={n.id}
                    data-testid={`notification-${n.id}`}
                    className={`rounded-md border p-4 ${
                      n.read ? "bg-white border-zinc-400" : "bg-white border-[#5EA529] shadow-sm"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                      <div className="flex items-center gap-2 flex-wrap">
                        {!n.read && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-white bg-red-600 rounded-full px-2 py-0.5">
                            Nuevo
                          </span>
                        )}
                        <Star className="h-4 w-4 text-[#5EA529]" />
                        <h3 className="font-mono font-bold text-black">
                          {n.activator}
                        </h3>
                        <span className="text-xs text-zinc-600 font-mono">
                          ha conseguido “{n.diploma_name}”
                        </span>
                      </div>
                      <span className="text-xs font-mono text-zinc-600 shrink-0">
                        {new Date(n.created_at).toLocaleString("es-ES")}
                      </span>
                    </div>
                    <p className="text-sm text-zinc-800 mt-2 border-t border-zinc-200 pt-3 font-mono">
                      {n.activations} activaciones · umbral {n.required}
                    </p>
                    <div className="flex gap-2 mt-3">
                      {!n.read && (
                        <Button
                          onClick={() => markNotifRead(n.id)}
                          variant="outline"
                          size="sm"
                          className="border-zinc-400 bg-white hover:bg-zinc-100 rounded-sm text-black"
                          data-testid={`mark-notif-read-${n.id}`}
                        >
                          <Check className="h-3.5 w-3.5 mr-1.5" /> Marcar leído
                        </Button>
                      )}
                      {n.read && (
                        <span className="text-xs text-zinc-500 font-mono inline-flex items-center gap-1">
                          <MailOpen className="h-3.5 w-3.5" /> Leído
                        </span>
                      )}
                      <Button
                        onClick={() => deleteNotification(n.id)}
                        variant="ghost"
                        size="sm"
                        className="text-zinc-600 hover:text-red-400 hover:bg-transparent ml-auto"
                        data-testid={`delete-notification-${n.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Diploma editor modal (custom, no portal to avoid React 19 removeChild issue) */}
      {editing !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="diploma-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setEditing(null)} />
          <div className="relative bg-[#D6D3D1] border border-zinc-400 rounded-md max-w-lg w-full max-h-[90vh] overflow-y-auto p-6" role="dialog" aria-modal="true">
            <div className="mb-4">
              <h2 className="font-mono tracking-tight text-lg font-semibold">
                {editing && editing.id ? "Editar diploma" : "Nuevo diploma"}
              </h2>
              <p className="text-zinc-600 text-sm">
                Configura el premio, sus requisitos y el texto del certificado.
              </p>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-600">Tipo</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({
                    ...form,
                    type: v,
                    required_contacts: v === "qsl" ? 1 : form.required_contacts,
                    overlay_mode: v === "multidiploma" ? "callsign" : form.overlay_mode,
                  })}>
                    <SelectTrigger data-testid="form-type-select" className={inputCls}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-zinc-400 text-zinc-900">
                      <SelectItem value="qsl" className="text-zinc-900">QSL (1 contacto)</SelectItem>
                      <SelectItem value="diploma" className="text-zinc-900">Diploma (varios)</SelectItem>
                      <SelectItem value="activator" className="text-zinc-900">Activador (nº de activaciones)</SelectItem>
                      <SelectItem value="multidiploma" className="text-zinc-900">Multidiploma (4 niveles progresivos)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.type === "activator" ? (
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-zinc-600">Activaciones req.</Label>
                    <Input
                      data-testid="form-required-activations-input"
                      type="number" min={1}
                      value={form.required_activations || 0}
                      onChange={(e) => setForm({ ...form, required_activations: e.target.value })}
                      className={inputCls}
                      placeholder="10 / 20 / 30 / 40"
                    />
                  </div>
                ) : form.type === "multidiploma" ? (
                  <div className="text-xs text-zinc-600 flex items-end pb-1.5">
                    Los contactos requeridos se definen por nivel más abajo.
                  </div>
                ) : (
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-zinc-600">Contactos req.</Label>
                    <Input data-testid="form-required-input" type="number" min={1} disabled={form.type === "qsl"} value={form.required_contacts} onChange={(e) => setForm({ ...form, required_contacts: e.target.value })} className={`${inputCls} disabled:opacity-50`} />
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-zinc-600">Nombre</Label>
                <Input data-testid="form-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="World Cancer Day" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-600">Indicativo activado</Label>
                  <Input data-testid="form-station-input" value={form.station_callsign} onChange={(e) => setForm({ ...form, station_callsign: e.target.value.toUpperCase() })} className={inputCls} placeholder="EG3WCD" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-600">Subtítulo</Label>
                  <Input data-testid="form-subtitle-input" value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className={inputCls} placeholder="World Cancer Day" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-600">Banda (opcional)</Label>
                  <Input data-testid="form-band-input" value={form.required_band} onChange={(e) => setForm({ ...form, required_band: e.target.value })} className={inputCls} placeholder="20m" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-600">Modo (opcional)</Label>
                  <Input data-testid="form-mode-input" value={form.required_mode} onChange={(e) => setForm({ ...form, required_mode: e.target.value })} className={inputCls} placeholder="SSB" />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-zinc-600">
                  Inscripción (usa {"{callsign}"} para el indicativo del receptor)
                </Label>
                <Textarea data-testid="form-inscription-input" rows={4} value={form.inscription} onChange={(e) => setForm({ ...form, inscription: e.target.value })} className={inputCls} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-zinc-600">
                  Bases del diploma (público)
                </Label>
                <Textarea
                  data-testid="form-bases-input"
                  rows={6}
                  value={form.bases}
                  onChange={(e) => setForm({ ...form, bases: e.target.value })}
                  className={inputCls}
                  placeholder="Explica las condiciones para conseguir este diploma/QSL: fechas, bandas, modos, número de contactos, cómo participar, etc."
                />
                <p className="text-xs text-zinc-600 mt-1.5">
                  Este texto se mostrará en la página pública “Diplomas activos” junto a la imagen de la plantilla.
                </p>
              </div>
              {form.type === "multidiploma" && (
                <div className="rounded-sm border border-zinc-400 bg-white p-4 space-y-3">
                  <div>
                    <p className="text-sm font-mono text-black font-semibold">Niveles del multidiploma</p>
                    <p className="text-xs text-zinc-600">Define 4 niveles progresivos. Puedes cambiar el nombre y el número de contactos requeridos. La misma posición del indicativo se aplicará a las 4 plantillas.</p>
                  </div>
                  <div className="space-y-2">
                    {(form.levels || []).map((lv, i) => (
                      <div key={i} className="grid grid-cols-12 gap-2 items-center">
                        <div className="col-span-4">
                          <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-600">Nombre</Label>
                          <Input
                            data-testid={`level-name-${i}`}
                            value={lv.name}
                            onChange={(e) => setForm({
                              ...form,
                              levels: form.levels.map((x, idx) => idx === i ? { ...x, name: e.target.value } : x),
                            })}
                            className={inputCls}
                          />
                        </div>
                        <div className="col-span-3">
                          <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-600">Contactos req.</Label>
                          <Input
                            data-testid={`level-required-${i}`}
                            type="number" min={1}
                            value={lv.required_contacts}
                            onChange={(e) => setForm({
                              ...form,
                              levels: form.levels.map((x, idx) => idx === i ? { ...x, required_contacts: Number(e.target.value) || 0 } : x),
                            })}
                            className={inputCls}
                          />
                        </div>
                        <div className="col-span-5 flex items-end gap-2 pt-1">
                          {editing && editing.id ? (
                            <>
                              <Button
                                data-testid={`level-upload-${i}`}
                                type="button" variant="outline"
                                onClick={() => pickLevelTemplate(editing.id, i)}
                                className="rounded-sm border-zinc-400 text-xs"
                              >
                                {lv.has_template ? "Cambiar plantilla" : "Subir plantilla"}
                              </Button>
                              {lv.has_template && (
                                <div className="h-10 w-14 border border-zinc-300 rounded-sm overflow-hidden bg-zinc-100">
                                  <img
                                    src={`${API}/diplomas/${editing.id}/template?level_index=${i}&t=${Date.now()}`}
                                    alt={lv.name}
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                              )}
                            </>
                          ) : (
                            <p className="text-xs text-zinc-500 italic">Guarda primero para subir plantilla</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {form.type !== "multidiploma" && (
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-600">Diseño del certificado</Label>
                  <Select value={form.overlay_mode} onValueChange={(v) => setForm({ ...form, overlay_mode: v })}>
                    <SelectTrigger data-testid="form-overlay-select" className={inputCls}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-zinc-400 text-zinc-900">
                      <SelectItem value="full" className="text-zinc-900">Texto automático (indicativo, subtítulo e inscripción)</SelectItem>
                      <SelectItem value="callsign" className="text-zinc-900">Solo indicativo (plantilla con su propio texto)</SelectItem>
                      <SelectItem value="qsl_card" className="text-zinc-900">QSL – rellenar casillas (indicativo, fecha, hora, banda, modo, RST)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-zinc-600 mt-1.5">
                    Usa “Solo indicativo” cuando la plantilla ya trae todo el texto y solo hay que rellenar el indicativo en el hueco.
                  </p>
                </div>
              )}
              {(form.type === "diploma" || form.type === "activator" || form.type === "multidiploma") && form.overlay_mode === "callsign" && (
                <div className="rounded-sm border border-zinc-400 bg-white p-4 space-y-4">
                  <div>
                    <p className="text-sm font-mono text-black font-semibold">Posición del indicativo</p>
                    <p className="text-xs text-zinc-600">Ajusta dónde queda el indicativo del receptor sobre la plantilla del diploma.</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs uppercase tracking-[0.2em] text-zinc-600">Horizontal (X): {Math.round((form.callsign_x || 0.5) * 100)}%</Label>
                      <Slider
                        data-testid="callsign-x-slider"
                        min={0} max={100} step={1}
                        value={[Math.round((form.callsign_x || 0.5) * 100)]}
                        onValueChange={(v) => setForm({ ...form, callsign_x: (v[0] || 0) / 100 })}
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-[0.2em] text-zinc-600">Vertical (Y): {Math.round((form.callsign_y || 0.5) * 100)}%</Label>
                      <Slider
                        data-testid="callsign-y-slider"
                        min={0} max={100} step={1}
                        value={[Math.round((form.callsign_y || 0.5) * 100)]}
                        onValueChange={(v) => setForm({ ...form, callsign_y: (v[0] || 0) / 100 })}
                        className="mt-2"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-zinc-600">Tamaño: {Math.round((form.callsign_size || 0.10) * 100)}% del alto</Label>
                    <Slider
                      data-testid="callsign-size-slider"
                      min={3} max={20} step={1}
                      value={[Math.round((form.callsign_size || 0.10) * 100)]}
                      onValueChange={(v) => setForm({ ...form, callsign_size: (v[0] || 3) / 100 })}
                      className="mt-2"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs uppercase tracking-[0.2em] text-zinc-600">Fuente</Label>
                      <Select value={form.callsign_font || "mono"} onValueChange={(v) => setForm({ ...form, callsign_font: v })}>
                        <SelectTrigger data-testid="callsign-font-select" className={inputCls}><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white border-zinc-400 text-zinc-900">
                          <SelectItem value="mono" className="text-zinc-900">Mono (uniforme, tipo consola)</SelectItem>
                          <SelectItem value="serif" className="text-zinc-900">Serif (decorativa)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-[0.2em] text-zinc-600">Color</Label>
                      <div className="flex items-center gap-2 mt-0.5">
                        <input
                          data-testid="callsign-color-input"
                          type="color"
                          value={form.callsign_color || "#000000"}
                          onChange={(e) => setForm({ ...form, callsign_color: e.target.value })}
                          className="h-9 w-14 border border-zinc-400 rounded-sm cursor-pointer bg-white"
                        />
                        <Input
                          value={form.callsign_color || "#000000"}
                          onChange={(e) => setForm({ ...form, callsign_color: e.target.value })}
                          className={`${inputCls} font-mono uppercase`}
                          placeholder="#000000"
                        />
                      </div>
                    </div>
                  </div>
                  {(() => {
                    const isMulti = form.type === "multidiploma";
                    const hasSingleTpl = editing && editing.id && editing.has_template && !isMulti;
                    const multiHasAnyTpl = isMulti && editing && editing.id && (form.levels || []).some((l) => l.has_template);
                    const previewTplUrl = isMulti
                      ? (form.levels[previewLevel]?.has_template
                          ? `${API}/diplomas/${editing.id}/template?level_index=${previewLevel}&t=${Date.now()}`
                          : null)
                      : `${API}/diplomas/${editing.id}/template?t=${editing.id}`;
                    if (!hasSingleTpl && !multiHasAnyTpl) {
                      return (<p className="text-xs text-zinc-600">Sube una plantilla{isMulti ? " a algún nivel" : ""} para ver la vista previa.</p>);
                    }
                    return (
                      <div>
                        <div className="flex items-center justify-between">
                          <Label className="text-xs uppercase tracking-[0.2em] text-zinc-600">Vista previa</Label>
                          {isMulti && (
                            <div className="flex gap-1" data-testid="preview-level-tabs">
                              {(form.levels || []).map((lv, i) => (
                                <button
                                  key={i}
                                  type="button"
                                  data-testid={`preview-level-${i}`}
                                  onClick={() => setPreviewLevel(i)}
                                  disabled={!lv.has_template}
                                  className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm border ${
                                    previewLevel === i
                                      ? "bg-black text-white border-black"
                                      : "bg-white text-zinc-700 border-zinc-400"
                                  } disabled:opacity-40 disabled:cursor-not-allowed`}
                                >
                                  {lv.name || `N${i + 1}`}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        <div
                          data-testid="callsign-preview"
                          className="relative mt-1 border border-zinc-400 rounded-sm overflow-hidden bg-zinc-100"
                          style={{ aspectRatio: "3 / 2", containerType: "size" }}
                        >
                          {previewTplUrl ? (
                            <img
                              src={previewTplUrl}
                              alt="Plantilla"
                              className="absolute inset-0 w-full h-full object-contain"
                            />
                          ) : (
                            <div className="absolute inset-0 flex items-center justify-center text-xs text-zinc-500">
                              Este nivel aún no tiene plantilla
                            </div>
                          )}
                          <div
                            className="absolute pointer-events-none select-none whitespace-nowrap"
                            style={{
                              left: `${(form.callsign_x || 0.5) * 100}%`,
                              top: `${(form.callsign_y || 0.5) * 100}%`,
                              transform: "translate(-50%, -50%)",
                              color: form.callsign_color || "#000000",
                              fontFamily: (form.callsign_font === "serif")
                                ? "'Playfair Display', 'Georgia', serif"
                                : "'IBM Plex Mono', 'Courier New', monospace",
                              fontWeight: 900,
                              fontSize: `${(form.callsign_size || 0.10) * 100}cqh`,
                              lineHeight: 1,
                              letterSpacing: form.callsign_font === "mono" ? "0.02em" : "0.01em",
                            }}
                          >
                            {(form.station_callsign || "EA5XXX").toUpperCase()}
                          </div>
                        </div>
                        <p className="text-xs text-zinc-600 mt-1.5">
                          Vista previa aproximada. La posición configurada se aplicará por igual a {isMulti ? "los 4 niveles" : "la plantilla"}.
                        </p>
                      </div>
                    );
                  })()}
                </div>
              )}
              {form.type === "qsl" && form.overlay_mode === "qsl_card" && (
                <div className="rounded-sm border border-zinc-400 bg-white p-4 space-y-4">
                  <div>
                    <p className="text-sm font-mono text-black font-semibold">Posición de los datos del QSO</p>
                    <p className="text-xs text-zinc-600">Coloca cada dato (indicativo, fecha, UTC, banda, modo, RST) independientemente sobre tu plantilla.</p>
                  </div>
                  <div className="flex flex-wrap gap-1" data-testid="qsl-field-tabs">
                    {QSL_FIELD_ORDER.map((k) => (
                      <button
                        key={k}
                        type="button"
                        data-testid={`qsl-field-tab-${k}`}
                        onClick={() => setActiveQslField(k)}
                        className={`text-[11px] uppercase tracking-widest px-3 py-1.5 rounded-sm border transition-colors ${
                          activeQslField === k
                            ? "bg-black text-white border-black"
                            : "bg-white text-zinc-700 border-zinc-400 hover:bg-zinc-50"
                        }`}
                      >
                        {QSL_FIELD_LABELS[k]}
                      </button>
                    ))}
                  </div>
                  {(() => {
                    const cur = (form.qsl_fields && form.qsl_fields[activeQslField]) || DEFAULT_QSL_FIELDS[activeQslField];
                    const update = (patch) => setForm({
                      ...form,
                      qsl_fields: {
                        ...form.qsl_fields,
                        [activeQslField]: { ...cur, ...patch },
                      },
                    });
                    return (
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs uppercase tracking-[0.2em] text-zinc-600">Horizontal (X): {Math.round((cur.x || 0.5) * 100)}%</Label>
                            <Slider
                              data-testid="qsl-x-slider"
                              min={0} max={100} step={1}
                              value={[Math.round((cur.x || 0.5) * 100)]}
                              onValueChange={(v) => update({ x: (v[0] || 0) / 100 })}
                              className="mt-2"
                            />
                          </div>
                          <div>
                            <Label className="text-xs uppercase tracking-[0.2em] text-zinc-600">Vertical (Y): {Math.round((cur.y || 0.5) * 100)}%</Label>
                            <Slider
                              data-testid="qsl-y-slider"
                              min={0} max={100} step={1}
                              value={[Math.round((cur.y || 0.5) * 100)]}
                              onValueChange={(v) => update({ y: (v[0] || 0) / 100 })}
                              className="mt-2"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs uppercase tracking-[0.2em] text-zinc-600">Tamaño: {Math.round((cur.size || 0.045) * 1000) / 10}% del alto</Label>
                          <Slider
                            data-testid="qsl-size-slider"
                            min={2} max={15} step={1}
                            value={[Math.round((cur.size || 0.045) * 100)]}
                            onValueChange={(v) => update({ size: (v[0] || 2) / 100 })}
                            className="mt-2"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs uppercase tracking-[0.2em] text-zinc-600">Fuente</Label>
                            <Select value={cur.font || "mono"} onValueChange={(v) => update({ font: v })}>
                              <SelectTrigger data-testid="qsl-font-select" className={inputCls}><SelectValue /></SelectTrigger>
                              <SelectContent className="bg-white border-zinc-400 text-zinc-900">
                                <SelectItem value="mono" className="text-zinc-900">Mono (uniforme, tipo consola)</SelectItem>
                                <SelectItem value="serif" className="text-zinc-900">Serif (decorativa)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs uppercase tracking-[0.2em] text-zinc-600">Color</Label>
                            <div className="flex items-center gap-2 mt-0.5">
                              <input
                                data-testid="qsl-color-input"
                                type="color"
                                value={cur.color || "#1C1C28"}
                                onChange={(e) => update({ color: e.target.value })}
                                className="h-9 w-14 border border-zinc-400 rounded-sm cursor-pointer bg-white"
                              />
                              <Input
                                value={cur.color || "#1C1C28"}
                                onChange={(e) => update({ color: e.target.value })}
                                className={`${inputCls} font-mono uppercase`}
                                placeholder="#1C1C28"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                  {editing && editing.id && editing.has_template ? (
                    <div>
                      <Label className="text-xs uppercase tracking-[0.2em] text-zinc-600">Vista previa</Label>
                      <div
                        data-testid="qsl-preview"
                        className="relative mt-1 border border-zinc-400 rounded-sm overflow-hidden bg-zinc-100"
                        style={{ aspectRatio: "3 / 2", containerType: "size" }}
                      >
                        <img
                          src={`${API}/diplomas/${editing.id}/template?t=${editing.id}`}
                          alt="Plantilla QSL"
                          className="absolute inset-0 w-full h-full object-contain"
                        />
                        {QSL_FIELD_ORDER.map((k) => {
                          const fc = (form.qsl_fields && form.qsl_fields[k]) || DEFAULT_QSL_FIELDS[k];
                          return (
                            <div
                              key={k}
                              className="absolute pointer-events-none select-none whitespace-nowrap"
                              style={{
                                left: `${(fc.x || 0.5) * 100}%`,
                                top: `${(fc.y || 0.5) * 100}%`,
                                transform: "translate(-50%, -50%)",
                                color: fc.color || "#1C1C28",
                                fontFamily: fc.font === "serif"
                                  ? "'Playfair Display', 'Georgia', serif"
                                  : "'IBM Plex Mono', 'Courier New', monospace",
                                fontWeight: 700,
                                fontSize: `${(fc.size || 0.045) * 100}cqh`,
                                lineHeight: 1,
                                letterSpacing: fc.font === "mono" ? "0.02em" : "0.01em",
                              }}
                            >
                              {QSL_FIELD_SAMPLES[k]}
                            </div>
                          );
                        })}
                      </div>
                      <p className="text-xs text-zinc-600 mt-1.5">
                        Vista previa con datos de ejemplo sobre tu plantilla.
                      </p>
                    </div>
                  ) : (
                    <p className="text-xs text-zinc-600">Sube la plantilla QSL para ver la vista previa.</p>
                  )}
                </div>
              )}
              <div className="flex items-start justify-between gap-4 rounded-sm border border-zinc-400 bg-white p-3">
                <div>
                  <p className="text-sm font-mono text-black font-semibold">Duplicados válidos</p>
                  <p className="text-xs text-zinc-600">Si se desactiva, los contactos duplicados (mismo indicativo, fecha, banda y modo) no puntúan.</p>
                </div>
                <Switch data-testid="form-duplicates-switch" checked={form.allow_duplicates} onCheckedChange={(v) => setForm({ ...form, allow_duplicates: v })} className="data-[state=checked]:bg-[#5EA529] mt-1" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="ghost" onClick={() => setEditing(null)} className="text-zinc-600 hover:text-zinc-900">Cancelar</Button>
              <Button data-testid="save-diploma-btn" onClick={saveDiploma} disabled={saving || !form.name} className="bg-[#5EA529] hover:bg-[#4f8c22] text-white font-semibold rounded-sm">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Guardar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
